import React from 'react'
import { projetosService, RoadmapGroup, RoadmapItem, RoadmapLabel } from '../../services/projetos'
import { supabase } from '../../lib/supabase'

export type RoadmapFilters = {
  q: string
  from: string | null
  to: string | null
  group_id: string | null
  status: string | null
  label_id: string | null
  me: boolean
}

type RoadmapState = {
  loading: boolean
  items: RoadmapItem[]
  groups: RoadmapGroup[]
  labels: RoadmapLabel[]
  filters: RoadmapFilters
  view: 'timeline' | 'kanban'
}

type RoadmapActions = {
  setView: (v: 'timeline' | 'kanban') => void
  setFilters: (p: Partial<RoadmapFilters>) => void
  loadInitial: () => Promise<void>
  reload: () => Promise<void>
}

const defaultFilters: RoadmapFilters = {
  q: '',
  from: null,
  to: null,
  group_id: null,
  status: null,
  label_id: null,
  me: false
}

const RoadmapContext = React.createContext<(RoadmapState & RoadmapActions) | null>(null)

export const RoadmapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = React.useState<RoadmapState>({
    loading: true,
    items: [],
    groups: [],
    labels: [],
    filters: defaultFilters,
    view: 'timeline'
  })

  const setView = React.useCallback((v: 'timeline' | 'kanban') => {
    setState(prev => ({ ...prev, view: v }))
    const url = new URL(window.location.href)
    url.searchParams.set('view', v)
    window.history.replaceState({}, '', url.toString())
  }, [])

  const setFilters = React.useCallback((p: Partial<RoadmapFilters>) => {
    setState(prev => {
      const next = { ...prev, filters: { ...prev.filters, ...p } }
      const url = new URL(window.location.href)
      const f = next.filters
      const maybeSet = (k: keyof RoadmapFilters, v: any) => {
        if (v === null || v === '' || v === false) url.searchParams.delete(k as string)
        else url.searchParams.set(k as string, String(v))
      }
      maybeSet('q', f.q)
      maybeSet('from', f.from)
      maybeSet('to', f.to)
      maybeSet('group_id', f.group_id)
      maybeSet('status', f.status)
      maybeSet('label_id', f.label_id)
      maybeSet('me', f.me ? 1 : '')
      window.history.replaceState({}, '', url.toString())
      return next
    })
  }, [])

  const reload = React.useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }))
    let items = await projetosService.searchItems({
      q: state.filters.q,
      from: state.filters.from,
      to: state.filters.to,
      group_id: state.filters.group_id,
      status: state.filters.status,
      me: state.filters.me
    })
    
    // Buscar label_ids para todos os itens
    if (items.length > 0) {
      const itemIds = items.map(i => i.id)
      const { data: itemLabels } = await supabase
        .from('roadmap_item_labels')
        .select('item_id, label_id')
        .in('item_id', itemIds)
      
      // Agrupar labels por item_id
      const labelsByItemId = new Map<string, string[]>()
      ;(itemLabels || []).forEach((il: any) => {
        const current = labelsByItemId.get(il.item_id) || []
        labelsByItemId.set(il.item_id, [...current, il.label_id])
      })
      
      // Adicionar label_ids aos itens
      items = items.map(item => ({
        ...item,
        label_ids: labelsByItemId.get(item.id) || []
      }))
    }
    
    // Se houver filtro por label, filtrar
    if (state.filters.label_id) {
      items = items.filter(item => (item.label_ids || []).includes(state.filters.label_id!))
    }
    
    setState(prev => ({ ...prev, items, loading: false }))
  }, [state.filters])

  const loadInitial = React.useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }))
    const url = new URL(window.location.href)
    const view = (url.searchParams.get('view') as any) || 'timeline'
    const f: RoadmapFilters = {
      q: url.searchParams.get('q') || '',
      from: url.searchParams.get('from'),
      to: url.searchParams.get('to'),
      group_id: url.searchParams.get('group_id'),
      status: url.searchParams.get('status'),
      label_id: url.searchParams.get('label_id'),
      me: url.searchParams.get('me') === '1'
    }
    const [groups, labels, itemsRaw] = await Promise.all([
      projetosService.getGroups(),
      projetosService.getLabels(),
      projetosService.searchItems({
        q: f.q,
        from: f.from,
        to: f.to,
        group_id: f.group_id,
        status: f.status,
        me: f.me
      })
    ])
    
    // Buscar label_ids para todos os itens
    let items = itemsRaw
    if (items.length > 0) {
      const itemIds = items.map(i => i.id)
      const { data: itemLabels } = await supabase
        .from('roadmap_item_labels')
        .select('item_id, label_id')
        .in('item_id', itemIds)
      
      // Agrupar labels por item_id
      const labelsByItemId = new Map<string, string[]>()
      ;(itemLabels || []).forEach((il: any) => {
        const current = labelsByItemId.get(il.item_id) || []
        labelsByItemId.set(il.item_id, [...current, il.label_id])
      })
      
      // Adicionar label_ids aos itens
      items = items.map(item => ({
        ...item,
        label_ids: labelsByItemId.get(item.id) || []
      }))
    }
    
    // Se houver filtro por label, filtrar
    if (f.label_id) {
      items = items.filter(item => (item.label_ids || []).includes(f.label_id!))
    }
    
    setState(prev => ({ ...prev, groups, labels, items, filters: f, loading: false, view }))
  }, [])

  const value = React.useMemo(() => ({
    ...state,
    setView,
    setFilters,
    reload,
    loadInitial
  }), [state, setView, setFilters, reload, loadInitial])

  return <RoadmapContext.Provider value={value}>{children}</RoadmapContext.Provider>
}

export const useRoadmapStore = () => {
  const ctx = React.useContext(RoadmapContext)
  if (!ctx) throw new Error('useRoadmapStore deve ser usado dentro de <RoadmapProvider>')
  return ctx
}


